/**
 * DTW-style alignment of a sung pitch trace onto a curriculum `NotationTimeline`
 * (Phase 5, composition mode). Pure: no DOM, no I/O, no shared mutable state.
 *
 * This is not the classic "every sung frame must align 1:1 with warping" DTW —
 * it is a sequence-alignment DP (match / insert / delete, à la Needleman–Wunsch)
 * over (sung frame × target note) so that:
 *   - a held note naturally absorbs many sung frames (repeated "insert" steps),
 *   - a target note the singer skipped entirely can be left with an empty span
 *     ("delete" step, no frame forced onto it), and
 *   - the path is always monotonic in both sung time and note order.
 */
/** Fold cents (any octave, positive or negative) into [0, 1200). */
const fold = (c) => ((c % 1200) + 1200) % 1200;
/**
 * Octave-aware circular distance (cents) between a sung reading and a target
 * note's `targetCents`. `targetCents` in a `NotationTimeline` is cents-vs-Sa
 * *including* octave (so tara Sa is 1200, a note below Sa can be negative);
 * sung cents are folded to a single octave. We fold both onto the 1200¢ circle
 * and take the shorter way round, so e.g. target 1200 (tara Sa) is ~0¢ from a
 * sung reading of 0 or 1199 — the octave itself is deliberately not part of the
 * distance (composition-mode scoring only has one detected pitch per moment and
 * has no independent octave signal to check against).
 */
function circularCentsDistance(sungCents, targetCents) {
    const d = Math.abs(fold(sungCents) - fold(targetCents));
    return Math.min(d, 1200 - d);
}
/**
 * Multiplier applied to a transition frame's cost: mid-slide/gamaka pitch is not
 * good evidence of "which note is this", so such frames should barely sway which
 * note the DP assigns them to (context from steady neighbours should dominate).
 */
const TRANSITION_WEIGHT = 0.25;
/**
 * Fixed cost of leaving a target note with an empty span ("deleting" it from the
 * alignment because the take never sings it). Chosen well below the smallest
 * adjacent-swara gap in practice (typically 100–300¢) so a genuinely omitted
 * note is cheaper to skip than to force-fit onto a neighbour's pitch data, but
 * well above ordinary intonation noise on a correctly-attempted note (a few
 * tens of cents), so attempted-but-mistuned notes are graded, not marked missed.
 */
const SKIP_NOTE_COST = 180;
/**
 * Align a sung pitch trace onto a target curriculum timeline.
 *
 * `saHz` is accepted for interface symmetry with `scoreAlignment` (which needs
 * it to convert cents back to Hz for `gradePitch`) — `sung[].cents` here are
 * already Sa-relative, so alignment itself has no further use for it.
 */
export function dtwAlign(sung, target, saHz) {
    void saHz;
    const targetNotes = target.notes;
    const m = targetNotes.length;
    if (m === 0)
        return { notes: [] };
    const n = sung.length;
    const cost = (i, j) => {
        const frame = sung[i - 1];
        const note = targetNotes[j - 1];
        const base = circularCentsDistance(frame.cents, note.targetCents);
        return frame.transition ? base * TRANSITION_WEIGHT : base;
    };
    // D[i][j] = min cost aligning the first i sung frames onto the first j target
    // notes (every one of the j notes accounted for, either with frames or skipped).
    const D = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(Infinity));
    const back = Array.from({ length: n + 1 }, () => new Array(m + 1).fill('match'));
    D[0][0] = 0;
    for (let j = 1; j <= m; j++) {
        D[0][j] = D[0][j - 1] + SKIP_NOTE_COST;
        back[0][j] = 'delete';
    }
    // D[i][0] for i>0 stays Infinity: frames can't exist with zero notes to explain them.
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const c = cost(i, j);
            const matchCost = D[i - 1][j - 1] + c; // frame i starts note j
            const insertCost = D[i - 1][j] + c; // frame i continues note j (already started)
            const deleteCost = D[i][j - 1] + SKIP_NOTE_COST; // note j skipped, no frame yet
            let best = matchCost;
            let step = 'match';
            if (insertCost < best) {
                best = insertCost;
                step = 'insert';
            }
            if (deleteCost < best) {
                best = deleteCost;
                step = 'delete';
            }
            D[i][j] = best;
            back[i][j] = step;
        }
    }
    const frameIndicesByNote = Array.from({ length: m }, () => []);
    let i = n;
    let j = m;
    while (i > 0 || j > 0) {
        const step = back[i][j];
        if (step === 'match') {
            frameIndicesByNote[j - 1].push(i - 1);
            i--;
            j--;
        }
        else if (step === 'insert') {
            frameIndicesByNote[j - 1].push(i - 1);
            i--;
        }
        else {
            j--;
        }
    }
    const notes = targetNotes.map((note, idx) => {
        const frames = frameIndicesByNote[idx].slice().sort((a, b) => a - b);
        const sungCents = frames.map((f) => ({ tMs: sung[f].tMs, cents: sung[f].cents }));
        return {
            noteIndex: note.index,
            sungStartMs: sungCents.length ? sungCents[0].tMs : 0,
            sungEndMs: sungCents.length ? sungCents[sungCents.length - 1].tMs : 0,
            sungCents,
        };
    });
    return { notes };
}
//# sourceMappingURL=align.js.map